import { ArrowRight, ChevronDown, ChevronRight, MessageSquareOff, MessageSquareText, Pause, Square, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FlowMatchMode } from './flow-match-mode';
import { FlowPausedNotice } from './flow-paused-notice';
import FlowTargetTaskPicker, { MapPickModal } from './flow-target-task-picker';

import type { Task } from '@/types/experience';
import type { EdgeCategory, FlowAction } from '@agavi/blocks';

import { Button } from '@/components/ui/button';
import { EditableTitle } from '@/components/ui/editable-heading';
import { IconButton } from '@/components/ui/icon-button';
import { isGotoTargetValid, type ConditionChange, type ConditionContradiction, type RuleProblemSeverity } from '@/lib/flow-utils';

interface FlowEventCardProps {
  /** Omit where the group's own heading already names the card — a consolidated sensor rung sits
   *  under a "Poor Sensor Data" heading, and repeating it in bold beneath is the same name twice. */
  title?: string;
  /** Omit to render the title as fixed, non-editable text (the three built-in cards). */
  onTitleChange?: (title: string) => void;
  /** Omit on a built-in rung: its any/all semantics are the ladder's, not a teacher's choice. */
  matchMode?: 'any' | 'all';
  onMatchModeChange?: (mode: 'any' | 'all') => void;
  /**
   * The event's condition list, shown while expanded. Omit on a built-in rung, which has no
   * conditions of its own — what it catches is fixed, and `hint` says what that is.
   */
  conditions?: React.ReactNode;
  /**
   * One line under the title naming the feedback cards this rung catches.
   *
   * The built-in rungs used to carry an expandable block-by-block grid instead. The grid answered
   * "which of this task's blocks could be correct" — a question with the same answer on every task,
   * restated per block — and took a whole expanded card to do it.
   */
  hint?: React.ReactNode;
  /**
   * Short statement of what is wrong, whatever the reason.
   *
   * There used to be two of these — a red `problem` string and an amber `paused` flag — which meant
   * two colours and two presentations for the one question a teacher asks once: will this fire, and
   * if not why. `severity` below carries the difference they were really expressing.
   */
  problem?: string;
  /**
   * How bad, which decides the colour — see `RuleProblem` in flow-utils, which computes it.
   *
   * `error` is "can never fire": no conditions at all (`conditionsSatisfied` returns false on an
   * empty list rather than reading `all` over nothing as vacuously true, so the event is silently
   * inert), or a contradiction. `warning` is "would fire, but its meaning moved".
   */
  severity?: RuleProblemSeverity;
  /** Another event says exactly the same thing. Not a pause -- it still fires -- so it reads
   *  muted, and only when nothing more urgent is wrong. */
  duplicateMessage?: string;
  /** The same event, read in one line, for when the card is collapsed. Without it a collapsed
   *  Special event showed only its title, which a teacher chose and which therefore says whatever
   *  they meant rather than what the event matches. */
  summary?: React.ReactNode;
  action: FlowAction;
  onActionChange: (action: FlowAction) => void;
  tasks: Task[];
  currentTaskId: string;
  /** Only the correct through-line may END the experience, and only at a terminus (the last spine
   * task, or a branch). Passed through to the picker's "End the experience" option. */
  allowEnd?: boolean;
  /** This event's flow category (correct / incorrect / neutral-incomplete) + special-rule flag — passed
   * to the picker so the pick-on-map flow line matches Flow's colour + terminator. */
  category?: EdgeCategory;
  special?: boolean;
  /**
   * Nothing follows this task, so there is no sensible forward default to write.
   *
   * The cycler writes `next` in one click without ever opening the picker. At a terminus that means
   * the run stops, and only the correct path may do that — so the cycler would be a way around the
   * rule the picker enforces. Refusing the click is worse: a teacher still has every right to send
   * a wrong answer somewhere, they just cannot fall through to a task that is not there.
   *
   * So cycling to "go" opens the map and asks. Agavi picks nothing; the teacher does.
   */
  noNextTask?: boolean;
  /** The mastery through-line on a mid-spine task: correct is LOCKED to "next" (the spine can't be
   * rewired or severed from here) — rendered as fixed text with no cycler/picker. */
  locked?: boolean;
  /**
   * NOTHING REACHES THIS CARD, so there is nothing here to act on.
   *
   * Distinct from `paused`, which is amber and means "this would fire but its meaning moved" —
   * a state the teacher fixes ON the card. Ghosted means the repair is somewhere else entirely:
   * All Correct cannot fire because a BLOCK cannot reach its pass state, and no amount of
   * editing this card changes that. The owner's words: "there's nothing you can do about them".
   *
   * Dimmed rather than hidden, because the card still states a rule that is part of the task,
   * and removing it would make the ladder read as though the rung did not exist.
   */
  ghosted?: boolean;
  /** Create a new NAMED pathway task off the current task, wiring this action's goto to it.
   * Threaded down to the target-task picker; omit to hide the "create a new pathway task" option. */
  onCreateDetourTask?: (names: { pathwayName: string; taskTitle: string }) => Promise<string | null>;
  onDelete?: () => void;
  /**
   * Opens this exit's content, and says whether any is authored.
   *
   * Absent on a slot that is not an exit — a `stay` keeps the student here, so there is nothing
   * to say goodbye with and no control to offer. The card does NOT work out for itself whether
   * it is an exit: the panel owns that, because the same question decides whether the slot can
   * hold content at all.
   */
  exit?: { has: boolean; onOpen: () => void };
  /** Whether this card starts collapsed. The three built-in cards (Any Incomplete/Incorrect/All
   * Correct) rarely need review, so they default to collapsed; a Special event defaults to
   * expanded since a teacher just added it to configure. */
  defaultCollapsed?: boolean;
  /** True when any targeted condition's meaning has changed since it was last reviewed — deleted,
   * deactivated, reactivated, or reclassified (see isRulePaused/getConditionSignature in
   * flow-utils) — covering every way a condition can drift, not just one of them. A paused event
   * won't fire (resolveFlow refuses to match the evaluation-drift half of this) until it's
   * resolved: reassign the rule's conditions, delete the event, or accept it as-is. */
  paused?: boolean;
  /** The before/after diff to show while paused — one entry per changed condition. */
  changes?: ConditionChange[];
  /** Conditions on this rule that can never all be true together (see getContradictoryBlockIds
   * in flow-utils) — a structural problem present from the moment it was configured, not
   * something that changed later. When any of these exist, `onAccept` should be omitted by the
   * caller: there's nothing to bless here, only conditions to edit or an event to delete. */
  contradictions?: ConditionContradiction[];
  /** Re-freezes every condition's signature to its current state, clearing `paused` without
   * touching `conditions` — the "accept the new setup" resolution, distinct from editing the
   * rule's conditions directly or deleting the event outright. Omit when `contradictions` is
   * non-empty — accepting a guaranteed contradiction wouldn't resolve anything. */
  onAccept?: () => void;
  /**
   * PUT THIS PROBLEM DOWN, or pick it back up — for a fault whose repair is not on this card.
   *
   * `onAccept` blesses a rule's CONDITIONS, re-freezing their signatures; this blesses the
   * card's `problem` sentence and nothing else. A starved All Correct is the case it exists
   * for: the rung cannot fire, the repair is in a block, and starving it is often the whole
   * design — a splitter that routes every option by rule starves it deliberately. The owner:
   * "otherwise it becomes noise that people start to ignore even when there's a real problem".
   *
   * `problemAccepted` keeps the sentence and drops the alarm, so the card still says why it is
   * inert. The button then offers the way back, because a dismissal nobody can undo is its own
   * trapdoor.
   */
  onToggleProblemAccepted?: () => void;
  problemAccepted?: boolean;
}

export function FlowEventCard({
  title, onTitleChange, matchMode, onMatchModeChange, conditions, hint, action, onActionChange,
  tasks, currentTaskId, allowEnd, category, special, locked, ghosted, noNextTask, onCreateDetourTask, onDelete, exit, defaultCollapsed = false, paused, problem, severity, duplicateMessage, summary, changes, contradictions, onAccept,
  onToggleProblemAccepted, problemAccepted,
}: FlowEventCardProps) {
  const { t } = useTranslation();
  const [deleteArmed, setDeleteArmed] = useState(false);
  const [pickingTarget, setPickingTarget] = useState(false);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const collapseToggleRef = useRef<HTMLButtonElement>(null);

  const isStop = action.type === 'stay';
  // Stay vs go is NOT a correct/incorrect distinction — colouring it red/green makes the eye read a
  // valence that isn't there (a "go" on an Any-Incorrect event looked reassuringly "correct"). Keep
  // the cycler NEUTRAL and let the symbol carry the meaning: ■ = stay (retry in place), → = advance/
  // redirect. Paused stays amber — that IS a real warning.
  const actionColor = paused
    ? 'border-ws-warning-fg/40 text-ws-warning-fg'
    : 'border-ws-border-strong text-ws-text';

  function cycleAction() {
    if (paused || locked || ghosted) return;
    if (isStop && noNextTask && !allowEnd) { setPickingTarget(true); return; }
    onActionChange(isStop ? { type: 'next' } : { type: 'stay' });
  }

  /**
   * What a severity looks like, in one place.
   *
   * The explanation box used to be amber whatever it was explaining, so a contradiction — which
   * can never fire and needs editing — was described in the colour reserved for "check this still
   * says what you meant", while the rows it referred to were red. The detail has to match what it
   * is about.
   */
  const SEVERITY_BOX: Record<RuleProblemSeverity, string> = {
    error: 'border-ws-error-fg/40 bg-ws-error-bg/20',
    warning: 'border-ws-warning-fg/30 bg-ws-warning-fg/[.05]',
  };

  return (
    /*
     * Tinted only while COLLAPSED.
     *
     * Closed, the card is all a teacher can see, so it has to carry the fact that something is
     * wrong. Open, it must not: the offending condition rows tint themselves, and a whole card in
     * amber behind them says "something here" while hiding which. One signal at a time, at the
     * smallest scope that can carry it.
     */
    /*
     * Banded like a Change card, but the split had to move first.
     *
     * The obvious attempt banded the existing header, which on this card holds the cycler, the
     * title AND the target picker -- the whole control surface. The built-in events carry no
     * conditions, so that left a filled 57px strip over an empty padded box, and a column of them
     * read as grey rules with the cards gone. The band is not the problem; what belongs in it is.
     * The header is the card's identity: which event this is, and where it sends the student.
     * The body is the hint and the conditions.
     *
     * `shrink-0` is required BY `overflow-hidden`, not merely tidy alongside it: an overflowing
     * flex item has an automatic minimum size of zero, so the column squashed these cards to
     * their two border pixels and clipped 125px of content. Any Incomplete and All Correct went
     * first, which is what "collapsed beyond view" was.
     */
    <div className={`flex shrink-0 flex-col overflow-hidden rounded-xl border transition-colors ${
      /* Opacity only: the colours below still carry the severity, and dimming the whole card is
         what says "read this, do not try to fix it here". */
      ghosted ? 'opacity-55 ' : ''}${
      deleteArmed ? 'border-ws-warning-fg bg-ws-warning-bg/40'
        : !collapsed ? 'border-ws-border'
        : severity === 'error' ? 'border-ws-error-fg bg-ws-error-bg/20'
        : severity === 'warning' ? 'border-ws-warning-fg bg-ws-warning-fg/[.04]'
        : 'border-ws-border'
    }`}>
      <div className={`flex items-start gap-2 border-b px-3 py-2 ${
        deleteArmed || (collapsed && severity) ? 'border-ws-border/60' : 'border-ws-border bg-ws-btn'
      }`}>
        {/* IconButton for the metrics, focus rule and hover, with the border and state colour on
            top: this is the one Flow control the shared variants have no equivalent of — an
            outlined square that carries the action's own state. */}
        <IconButton
          type='button'
          onClick={cycleAction}
          disabled={paused || locked || ghosted}
          aria-label={locked ? t('task.flow.card.lockedToNext') : paused ? t('task.flow.card.actionPausedLabel') : t('task.flow.card.actionCycleLabel', { state: isStop ? t('task.flow.card.stop') : t('task.flow.card.go') })}
          title={locked ? t('task.flow.card.lockedToNext') : paused ? t('task.flow.card.actionPausedLabel') : undefined}
          className={`self-center border transition-colors ${paused || locked ? 'cursor-not-allowed' : 'hover:bg-ws-hover'} ${actionColor}`}
        >
          {paused ? <Pause aria-hidden='true' className='h-3.5 w-3.5' /> : isStop ? <Square aria-hidden='true' className='h-3.5 w-3.5' fill='currentColor' /> : <ArrowRight aria-hidden='true' className='h-4 w-4' strokeWidth={2.5} />}
        </IconButton>

        {/* Name and destination, stacked tight beside the cycler. Both are header: the card's
            identity is WHICH event this is and WHERE it sends the student, and splitting the two
            put half the sentence above the band's edge and half below it.

            `px-1 py-0.5 -mx-1` matches FlowTargetTaskPicker's inline trigger exactly -- cycling
            stay -> go swaps the text for the button, and a 4px difference made the card jump on
            every cycle. A locked correct through-line reads exactly as a chosen one would: it IS
            the positional default, and saying so in different words than the picker uses made one
            behaviour look like two. The padlock lives in the cycler's disabled state. */}
        <div className='flex min-w-0 flex-1 flex-col'>
          {onTitleChange ? (
            <EditableTitle
              size='sm'
              value={title ?? ''}
              onCommit={onTitleChange}
              ariaLabel={t('task.flow.card.titleLabel')} />
          ) : title ? (
            <span className='min-w-0 truncate text-sm font-semibold text-ws-text'>{title}</span>
          ) : null}

          {locked ? (
            <p className='truncate px-1 py-0.5 -mx-1 text-xs italic text-ws-muted'>
              {t(noNextTask ? 'task.flow.card.defaultEnd' : 'task.flow.card.defaultNext')}
            </p>
          ) : isStop ? (
            <p className='truncate px-1 py-0.5 -mx-1 text-xs italic text-ws-muted'>{t('task.flow.card.stayOnTask')}</p>
          ) : (
            <FlowTargetTaskPicker
              tasks={tasks}
              currentTaskId={currentTaskId}
              action={action}
              onChange={onActionChange}
              isInvalid={!isGotoTargetValid(action, tasks)}
              onCreateDetourTask={onCreateDetourTask}
              allowEnd={allowEnd}
              category={category}
              special={special}
              inline
            />
          )}
        </div>

        {/* EXIT CONTENT, in the header beside the collapse toggle.
            The built-in cards pass no `conditions`, so they draw no chevron and this is the only
            control on that side — which is where 83% of the exits in the app actually are.

            TWO GLYPHS, not one glyph in two weights. It was `MessageSquareText` throughout,
            distinguished by colour and a half-step of stroke width, and at 16px those are not a
            distinction — you cannot tell "has a summary" from "has none" without another card
            beside it to compare against, and most of the time there is not one.

            A struck-through speech bubble says empty on its own, the way every muted-audio and
            hidden-eye glyph in the app does, and it stays legible to someone who cannot use the
            colour. Empty is still perfectly valid — the icon is a fact, not a warning, which is
            why it is the plain muted tone and not the error one. */}
        {exit && (
          <IconButton
            type='button'
            onClick={exit.onOpen}
            aria-label={exit.has ? t('task.flow.exit.editLabel') : t('task.flow.exit.addLabel')}
            title={exit.has ? t('task.flow.exit.editLabel') : t('task.flow.exit.addLabel')}
            className={`-mx-1 self-center ${exit.has ? 'text-ws-primary' : ''}`}
          >
            {exit.has
              ? <MessageSquareText aria-hidden='true' className='h-4 w-4' strokeWidth={2.5} />
              : <MessageSquareOff aria-hidden='true' className='h-4 w-4' strokeWidth={2} />}
          </IconButton>
        )}

        {conditions && (
          <IconButton
            ref={collapseToggleRef}
            type='button'
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? t('task.flow.card.expandLabel') : t('task.flow.card.collapseLabel')}
            className='-mx-1 self-center'
          >
            {collapsed ? <ChevronRight className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}
          </IconButton>
        )}
      </div>

      <div className='flex flex-col gap-3 p-3'>
      {hint}

      {pickingTarget && (
        <MapPickModal
          tasks={tasks}
          currentTaskId={currentTaskId}
          selectedId={null}
          category={category}
          special={special}
          onPick={(id) => { onActionChange({ type: 'goto', targetTaskId: id }); setPickingTarget(false); }}
          onClose={() => setPickingTarget(false)}
        />
      )}

      {conditions && !collapsed && matchMode && onMatchModeChange && (
        <FlowMatchMode value={matchMode} onChange={onMatchModeChange} />
      )}

      {conditions && !collapsed && conditions}
      {conditions && collapsed && summary}

      {/* Delete lives only in the expanded view — the collapsed card's whole bottom area is
          reserved for the paused resolution buttons when paused, and stays empty otherwise. */}
      {(paused || duplicateMessage || onToggleProblemAccepted || (onDelete && !collapsed)) && (
        <div className='flex flex-col gap-2'>
          <div className='flex items-center gap-2'>
            <div className='flex flex-1 items-center gap-1.5'>
              <FlowPausedNotice
                pausedMessage={paused ? problem ?? t('task.flow.rule.paused') : undefined}
                duplicateMessage={duplicateMessage}
                muted={problemAccepted}
              />
            </div>

            {/* Beside the sentence it acts on, not under the card: it is the answer to that one
                line, and the foot of the card is already where a problem is resolved. */}
            {onToggleProblemAccepted && (
              <Button
                variant='secondary'
                size='sm'
                onClick={onToggleProblemAccepted}
                className='shrink-0'
              >
                {t(problemAccepted
                  ? 'task.flow.rule.problemRestore' : 'task.flow.rule.problemDismiss')}
              </Button>
            )}

            {onDelete && !collapsed && (
              <>
                <div aria-live='assertive' aria-atomic='true' className='sr-only'>
                  {deleteArmed ? t('blocks.actions.deleteArmed') : ''}
                </div>
                <IconButton
                  type='button'
                  variant='destructive'
                  isArmed={deleteArmed}
                  onClick={() => {
                    if (!deleteArmed) { setDeleteArmed(true); return; }
                    onDelete();
                  }}
                  onPointerLeave={(e) => { if (e.pointerType !== 'touch' && deleteArmed) setDeleteArmed(false); }}
                  onBlur={() => { if (deleteArmed) setDeleteArmed(false); }}
                  onKeyDown={(e) => { if (e.key === 'Escape' && deleteArmed) { e.stopPropagation(); setDeleteArmed(false); } }}
                  aria-label={deleteArmed ? t('task.flow.rule.deleteConfirmLabel', { title }) : t('task.flow.rule.deleteLabel', { title })}
                >
                  <Trash2 className='h-4 w-4' />
                </IconButton>
              </>
            )}
          </div>

          {/* No Update button here any more. Its only behaviour was `setCollapsed(false)` — it
              expanded the card and nothing else, which the caret beside the title already does.
              Two controls for one act, and the one that looked like a resolution did not resolve
              anything. Accept still lives below, where the detail it blesses is visible. */}

          {paused && !collapsed && (((changes && changes.length > 0)) || (contradictions && contradictions.length > 0)) && (
            <div className={`flex flex-col gap-1.5 rounded-lg border p-2 ${SEVERITY_BOX[severity ?? 'warning']}`}>
              <ul className='flex flex-col gap-1 text-xs text-ws-text'>
                {contradictions?.map((c, i) => (
                  <li key={`contradiction-${i}`}>
                    {t('task.flow.rule.contradictionItem', { block: c.blockLabel, states: c.stateLabels.join(` ${t('task.flow.card.connectorAnd')} `) })}
                  </li>
                ))}
                {changes?.map((c, i) => (
                  <li key={`change-${i}`}>
                    {t('task.flow.rule.changeItem', { block: c.blockLabel, state: c.stateLabel, after: c.after })}
                  </li>
                ))}
              </ul>
              {onAccept && (
                <Button
                  variant='warning'
                  size='sm'
                  onClick={onAccept}
                  aria-label={t('task.flow.rule.acceptLabel', { title })}
                  className='self-end'
                >
                  {t('task.flow.rule.accept')}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
